import firebase, { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import { VideoLibraryItem } from "../../types/Community";

export type CommunitySlideStoryType = {
  type: string;
  label: string;
  description?: string;
  example?: string;
  icon?: string;
  isCustom?: boolean;
};

export type CommunitySlideStorySettings = {
  slideStoryTypes?: CommunitySlideStoryType[];
  slideStoryPromptTemplates?: Record<string, string>;
};

export type CommunityVideoLibrary = {
  items: VideoLibraryItem[];
};

const SLIDE_DOC_ID = "slides";
const VIDEO_LIBRARY_DOC_ID = "videoLibrary";
const VIDEO_LIBRARY_ITEMS_FIELD = "items";

const LEGACY_SLIDE_FIELDS = ["slideStoryTypes", "slideStoryPromptTemplates"] as const;
const LEGACY_VIDEO_LIBRARY_FIELDS = ["videoLibrary"] as const;
const LEGACY_COMMUNITY_MEDIA_FIELDS = [
  ...LEGACY_SLIDE_FIELDS,
  ...LEGACY_VIDEO_LIBRARY_FIELDS,
] as const;

type LegacySlideField = (typeof LEGACY_SLIDE_FIELDS)[number];
type LegacyVideoLibraryField = (typeof LEGACY_VIDEO_LIBRARY_FIELDS)[number];
type LegacyCommunityMediaField = (typeof LEGACY_COMMUNITY_MEDIA_FIELDS)[number];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const communityRef = (communityId: string) =>
  firestore.collection(DB_PATH.COMMUNITY).doc(communityId);

export const getCommunitySlideSettingsRef = (communityId: string) =>
  communityRef(communityId).collection(DB_PATH.COMMUNITY_MEDIA).doc(SLIDE_DOC_ID);

export const getCommunityVideoLibraryRef = (communityId: string) =>
  communityRef(communityId).collection(DB_PATH.COMMUNITY_MEDIA).doc(VIDEO_LIBRARY_DOC_ID);

const normalizeSlideStoryType = (value: unknown): CommunitySlideStoryType | null => {
  if (!isRecord(value)) return null;
  const type = typeof value.type === "string" ? value.type : "";
  const label = typeof value.label === "string" ? value.label : "";
  if (!type || !label) return null;

  const normalized: CommunitySlideStoryType = { type, label };
  if (typeof value.description === "string") normalized.description = value.description;
  if (typeof value.example === "string") normalized.example = value.example;
  if (typeof value.icon === "string") normalized.icon = value.icon;
  if (typeof value.isCustom === "boolean") normalized.isCustom = value.isCustom;
  return normalized;
};

const normalizeStringMap = (value: unknown): Record<string, string> | undefined => {
  if (!isRecord(value)) return undefined;
  const normalized: Record<string, string> = {};
  Object.entries(value).forEach(([key, entryValue]) => {
    if (typeof entryValue === "string") normalized[key] = entryValue;
  });
  return normalized;
};

const normalizeSlideSettings = (value: unknown): CommunitySlideStorySettings => {
  if (!isRecord(value)) return {};

  const normalized: CommunitySlideStorySettings = {};

  if (Array.isArray(value.slideStoryTypes)) {
    const entries = value.slideStoryTypes
      .map(normalizeSlideStoryType)
      .filter((entry): entry is CommunitySlideStoryType => entry !== null);
    normalized.slideStoryTypes = entries;
  }

  const templates = normalizeStringMap(value.slideStoryPromptTemplates);
  if (templates !== undefined) {
    normalized.slideStoryPromptTemplates = templates;
  }

  return normalized;
};

const normalizeVideoLibraryItems = (value: unknown): VideoLibraryItem[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is VideoLibraryItem =>
    Boolean(entry) &&
    typeof entry === "object" &&
    typeof (entry as VideoLibraryItem).id === "string" &&
    typeof (entry as VideoLibraryItem).mediaUrl === "string"
  );
};

const extractLegacySlideSettings = (communityData: unknown): CommunitySlideStorySettings => {
  if (!isRecord(communityData)) return {};
  const payload: Partial<Record<LegacySlideField, unknown>> = {};
  LEGACY_SLIDE_FIELDS.forEach((field) => {
    if (hasOwn(communityData, field)) payload[field] = communityData[field];
  });
  return normalizeSlideSettings(payload);
};

const extractLegacyVideoLibraryItems = (communityData: unknown): VideoLibraryItem[] => {
  if (!isRecord(communityData)) return [];
  return normalizeVideoLibraryItems(communityData.videoLibrary);
};

const buildLegacyDeletePayload = (
  fields: readonly LegacyCommunityMediaField[]
): Record<string, firebase.firestore.FieldValue> =>
  fields.reduce((payload, field) => {
    payload[field] = firebase.firestore.FieldValue.delete();
    return payload;
  }, {} as Record<string, firebase.firestore.FieldValue>);

export const hasLegacyCommunityMedia = (communityData: unknown): boolean =>
  isRecord(communityData) &&
  LEGACY_COMMUNITY_MEDIA_FIELDS.some((field) => hasOwn(communityData, field));

export const stripLegacyCommunityMedia = <T extends Record<string, unknown>>(
  communityData: T
): Omit<T, LegacyCommunityMediaField> => {
  const sanitized = { ...communityData };
  LEGACY_COMMUNITY_MEDIA_FIELDS.forEach((field) => {
    delete sanitized[field];
  });
  return sanitized;
};

const mergeMissingSlideFields = (
  legacyConfig: CommunitySlideStorySettings,
  currentConfig: CommunitySlideStorySettings
): CommunitySlideStorySettings => {
  const next: CommunitySlideStorySettings = {};
  if (legacyConfig.slideStoryTypes !== undefined && currentConfig.slideStoryTypes === undefined) {
    next.slideStoryTypes = legacyConfig.slideStoryTypes;
  }
  if (
    legacyConfig.slideStoryPromptTemplates !== undefined &&
    currentConfig.slideStoryPromptTemplates === undefined
  ) {
    next.slideStoryPromptTemplates = legacyConfig.slideStoryPromptTemplates;
  }
  return next;
};

const migrateLegacyCommunityMediaFromData = async (
  communityId: string,
  communityData: unknown,
  currentSlide: CommunitySlideStorySettings,
  currentVideoLibrary: VideoLibraryItem[]
): Promise<{ slide: CommunitySlideStorySettings; videoLibrary: VideoLibraryItem[] }> => {
  if (!hasLegacyCommunityMedia(communityData)) {
    return { slide: currentSlide, videoLibrary: currentVideoLibrary };
  }

  const legacySlide = extractLegacySlideSettings(communityData);
  const legacyVideoItems = extractLegacyVideoLibraryItems(communityData);

  const missingSlideFields = mergeMissingSlideFields(legacySlide, currentSlide);
  const shouldSeedVideoLibrary = currentVideoLibrary.length === 0 && legacyVideoItems.length > 0;

  const batch = firestore.batch();

  if (Object.keys(missingSlideFields).length) {
    batch.set(getCommunitySlideSettingsRef(communityId), missingSlideFields, { merge: true });
  }

  if (shouldSeedVideoLibrary) {
    batch.set(
      getCommunityVideoLibraryRef(communityId),
      { [VIDEO_LIBRARY_ITEMS_FIELD]: legacyVideoItems },
      { merge: true }
    );
  }

  batch.set(
    communityRef(communityId),
    buildLegacyDeletePayload(LEGACY_COMMUNITY_MEDIA_FIELDS),
    { merge: true }
  );

  await batch.commit();

  return {
    slide: { ...legacySlide, ...currentSlide },
    videoLibrary: shouldSeedVideoLibrary ? legacyVideoItems : currentVideoLibrary,
  };
};

export const getCommunitySlideSettings = async (
  communityId: string,
  options?: { communityData?: unknown }
): Promise<CommunitySlideStorySettings> => {
  if (!communityId) return {};

  const slideRef = getCommunitySlideSettingsRef(communityId);
  const fetchCommunity = options?.communityData === undefined;

  const [slideSnap, videoSnap, communitySnap] = await Promise.all([
    slideRef.get(),
    fetchCommunity ? getCommunityVideoLibraryRef(communityId).get() : Promise.resolve(null),
    fetchCommunity ? communityRef(communityId).get() : Promise.resolve(null),
  ]);

  const currentSlide = normalizeSlideSettings(slideSnap.data());
  const communityData = options?.communityData ?? communitySnap?.data();

  if (!hasLegacyCommunityMedia(communityData)) return currentSlide;

  const currentVideoLibrary = normalizeVideoLibraryItems(
    (videoSnap?.data() || {})[VIDEO_LIBRARY_ITEMS_FIELD]
  );

  const migrated = await migrateLegacyCommunityMediaFromData(
    communityId,
    communityData,
    currentSlide,
    currentVideoLibrary
  );
  return migrated.slide;
};

export const saveCommunitySlideSettings = async (
  communityId: string,
  settings: CommunitySlideStorySettings
): Promise<void> => {
  if (!communityId) return;
  const normalized = normalizeSlideSettings(settings);
  const batch = firestore.batch();
  batch.set(getCommunitySlideSettingsRef(communityId), normalized, { merge: true });
  batch.set(
    communityRef(communityId),
    buildLegacyDeletePayload(LEGACY_SLIDE_FIELDS),
    { merge: true }
  );
  await batch.commit();
};

export const getCommunityVideoLibraryItems = async (
  communityId: string,
  options?: { communityData?: unknown }
): Promise<VideoLibraryItem[]> => {
  if (!communityId) return [];

  const videoRef = getCommunityVideoLibraryRef(communityId);
  const fetchCommunity = options?.communityData === undefined;

  const [videoSnap, slideSnap, communitySnap] = await Promise.all([
    videoRef.get(),
    fetchCommunity ? getCommunitySlideSettingsRef(communityId).get() : Promise.resolve(null),
    fetchCommunity ? communityRef(communityId).get() : Promise.resolve(null),
  ]);

  const currentItems = normalizeVideoLibraryItems(
    (videoSnap.data() || {})[VIDEO_LIBRARY_ITEMS_FIELD]
  );
  const communityData = options?.communityData ?? communitySnap?.data();

  if (!hasLegacyCommunityMedia(communityData)) return currentItems;

  const currentSlide = normalizeSlideSettings(slideSnap?.data());
  const migrated = await migrateLegacyCommunityMediaFromData(
    communityId,
    communityData,
    currentSlide,
    currentItems
  );
  return migrated.videoLibrary;
};

export const VIDEO_LIBRARY_ITEMS_FIELD_NAME = VIDEO_LIBRARY_ITEMS_FIELD;

export const migrateLegacyCommunityMedia = async (
  communityId: string,
  communityData?: unknown
): Promise<void> => {
  if (!communityId) return;

  const fetchCommunity = communityData === undefined;
  const [slideSnap, videoSnap, communitySnap] = await Promise.all([
    getCommunitySlideSettingsRef(communityId).get(),
    getCommunityVideoLibraryRef(communityId).get(),
    fetchCommunity ? communityRef(communityId).get() : Promise.resolve(null),
  ]);

  const data = communityData ?? communitySnap?.data();
  if (!hasLegacyCommunityMedia(data)) return;

  await migrateLegacyCommunityMediaFromData(
    communityId,
    data,
    normalizeSlideSettings(slideSnap.data()),
    normalizeVideoLibraryItems((videoSnap.data() || {})[VIDEO_LIBRARY_ITEMS_FIELD])
  );
};
